import { NavLink, useNavigate } from "react-router-dom";

// MUI
import { AccountBalance, ArrowForward, Assignment, Business, BusinessCenter, Call, EmojiEvents, FormatQuote, NotificationsActive, Psychology, RateReview, Rocket, School, Speed, Star, TrendingUp, VerifiedUser, WorkspacePremium } from "@mui/icons-material";
import { Box, Button, Card, CardContent, Chip, CircularProgress, Container, Dialog, DialogContent, DialogTitle, Divider, Grid, IconButton, Stack, Typography, useTheme } from "@mui/material";
import axios from "axios";
import { useEffect, useState } from "react";
import { TypeAnimation } from "react-type-animation";

// Swiper
import "swiper/css";
import "swiper/css/pagination";
import { Autoplay, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

export default function Home() {
	const apiURL = process.env.REACT_APP_API;
	const assetsURL = process.env.REACT_APP_ASSETS;

	let navi = useNavigate();
	const theme = useTheme();

	const stats = [
		{ number: "50K+", label: "Students", icon: EmojiEvents },
		{ number: "100%", label: "Success Rate", icon: TrendingUp },
		{ number: "500+", label: "Courses", icon: Speed },
		{ number: "478+", label: "Expert Instructors", icon: School },
	];

	const [reviews, setReviews] = useState([]);
	const [totalReviews, setTotalReviews] = useState(0);
	const [teachers, setTeachers] = useState([]);
	const [notices, setNotices] = useState([]);
	const [noticeLoading, setNoticeLoading] = useState(true);
	const [selectedNotice, setSelectedNotice] = useState(null);

	useEffect(() => {
		fetchReviews();
		fetchTeachers();
		fetchNotices();
	}, [])
	const fetchReviews = async () => {
		try {
			const response = await axios.get(`${apiURL}/public/review/getall?limit=3`);
			if (response.data.status) {
				setReviews(response.data.data);
				setTotalReviews(response.data.pagination.totalRecords);
			}

		} catch (error) {
			console.log(error);
		}
	}

	const fetchTeachers = async () => {
		try {
			const response = await axios.get(`${apiURL}/public/teachers/getall`);
			if (response.data.status) {
				setTeachers(response.data.data);
			}
		} catch (error) {
			console.log(error);
		}
	}

	const fetchNotices = async () => {
		try {
			const response = await axios.get(`${apiURL}/public/notice/getall`);
			if (response.data.status) {
				setNotices(response.data.data);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setNoticeLoading(false);
		}
	}

	const NOTICE_TYPE_COLORS = {
		info: "#26a69a",
		message: "#0E3A43",
		warning: "#ffa726",
		alert: "#ef5350",
	};

	return (
		<>
			{/* Hero Section */}
			<Box
				sx={{
					position: "relative",
					minHeight: "95vh",
					display: "flex",
					alignItems: "center",
					justifyContent: "center",
					background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
					overflow: "hidden",
					pt: { xs: 4, md: 0 },
					"&::before": {
						content: '""',
						position: "absolute",
						top: "-50%",
						right: "-20%",
						width: "800px",
						height: "800px",
						borderRadius: "50%",
						background: `radial-gradient(circle, ${theme.palette.secondary.main}25 0%, transparent 70%)`,
						animation: "rotate 30s linear infinite",
					},
					"&::after": {
						content: '""',
						position: "absolute",
						bottom: "-30%",
						left: "-10%",
						width: "600px",
						height: "600px",
						borderRadius: "50%",
						background: `radial-gradient(circle, ${theme.palette.info.main}20 0%, transparent 70%)`,
						animation: "rotate 25s linear infinite reverse",
					},
					"@keyframes rotate": {
						"0%": { transform: "rotate(0deg)" },
						"100%": { transform: "rotate(360deg)" }
					}
				}}
			>
				{/* Floating geometric shapes */}
				<Box
					sx={{
						position: "absolute",
						top: "20%",
						right: "15%",
						width: "150px",
						height: "150px",
						background: `${theme.palette.secondary.main}15`,
						borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%",
						animation: "morph 8s ease-in-out infinite",
						"@keyframes morph": {
							"0%, 100%": { borderRadius: "30% 70% 70% 30% / 30% 30% 70% 70%" },
							"50%": { borderRadius: "70% 30% 30% 70% / 70% 70% 30% 30%" }
						}
					}}
				/>
				<Box
					sx={{
						position: "absolute",
						bottom: "25%",
						left: "10%",
						width: "100px",
						height: "100px",
						background: `${theme.palette.info.main}20`,
						borderRadius: "50%",
						animation: "float 6s ease-in-out infinite",
						"@keyframes float": {
							"0%, 100%": { transform: "translateY(0px)" },
							"50%": { transform: "translateY(-30px)" }
						}
					}}
				/>

				<Container maxWidth="lg" sx={{ position: "relative", zIndex: 1 }}>
					<Grid container spacing={6} alignItems="center">
						{/* Left Content */}
						<Grid item xs={12} md={7}>
							<Stack spacing={4}>
								{/* Badge */}
								<Box
									sx={{
										animation: "slideInLeft 0.8s ease-out",
										"@keyframes slideInLeft": {
											from: { opacity: 0, transform: "translateX(-50px)" },
											to: { opacity: 1, transform: "translateX(0)" }
										}
									}}
								>
									<Box
										sx={{
											display: "inline-flex",
											alignItems: "center",
											gap: 1,
											px: 2,
											py: 0.8,
											borderRadius: 50,
											background: `linear-gradient(135deg, ${theme.palette.secondary.main}30, ${theme.palette.info.main}30)`,
											border: `1px solid ${theme.palette.secondary.main}50`,
											backdropFilter: "blur(10px)",
										}}
									>
										<Box
											sx={{
												width: 8,
												height: 8,
												borderRadius: "50%",
												background: theme.palette.secondary.main,
												animation: "pulse 2s ease-in-out infinite",
												"@keyframes pulse": {
													"0%, 100%": { opacity: 1, transform: "scale(1)" },
													"50%": { opacity: 0.5, transform: "scale(1.2)" }
												}
											}}
										/>
										<Typography
											sx={{
												color: theme.palette.secondary.main,
												fontWeight: 600,
												fontSize: { xs: "0.7rem", md: "0.75rem" },
												letterSpacing: "0.5px"
											}}
										>
											TRANSFORM YOUR CAREER
										</Typography>
									</Box>
								</Box>

								{/* Main Heading */}
								<Box
									sx={{
										animation: "slideInLeft 0.8s ease-out 0.2s both",
									}}
								>
									<Typography
										sx={{
											fontSize: { xs: "2rem", md: "2.8rem", lg: "3.5rem" },
											fontWeight: 900,
											color: "#ffffff",
											lineHeight: 1.1,
											mb: 1.5,
											background: `linear-gradient(135deg, #ffffff 0%, ${theme.palette.secondary.main} 100%)`,
											WebkitBackgroundClip: "text",
											WebkitTextFillColor: "transparent",
											backgroundClip: "text",
										}}
									>
										Master the Future of Tech
									</Typography>
									<Typography
										sx={{
											fontSize: { xs: "0.9rem", md: "1.1rem" },
											color: "rgba(255, 255, 255, 0.85)",
											lineHeight: 1.7,
											fontWeight: 400,
											maxWidth: "600px"
										}}
									>
										Join thousands of students mastering cutting-edge skills in Web Development, AI, Data Science, and more.
									</Typography>
								</Box>

								{/* Typing Animation */}
								<Box
									sx={{
										animation: "slideInLeft 0.8s ease-out 0.4s both",
									}}
								>
									<Box
										sx={{
											p: 2.5,
											borderRadius: 2,
											background: "rgba(255, 255, 255, 0.08)",
											backdropFilter: "blur(20px)",
											border: `1px solid ${theme.palette.secondary.main}25`,
											position: "relative",
											overflow: "hidden",
											"&::before": {
												content: '""',
												position: "absolute",
												top: 0,
												left: 0,
												right: 0,
												height: "2px",
												background: `linear-gradient(90deg, ${theme.palette.secondary.main}, ${theme.palette.info.main})`,
											}
										}}
									>
										<Typography
											sx={{
												fontSize: { xs: "0.75rem", md: "0.85rem" },
												color: "rgba(255, 255, 255, 0.6)",
												mb: 1.5,
												fontFamily: "monospace",
												letterSpacing: "1px"
											}}
										>
											&gt; Currently trending:
										</Typography>
										<TypeAnimation
											sequence={[
												"Web Development",
												2000,
												"Artificial Intelligence",
												2000,
												"Data Science",
												2000,
												"Cloud Computing",
												2000,
												"Cybersecurity",
												2000
											]}
											style={{
												fontSize: "1.2em",
												fontWeight: "bold",
												color: theme.palette.secondary.main,
												textShadow: `0 0 30px ${theme.palette.secondary.main}50`
											}}
											repeat={Infinity}
										/>
									</Box>
								</Box>

								{/* CTA Buttons */}
								<Stack
									direction={{ xs: "column", sm: "row" }}
									spacing={2}
									sx={{
										animation: "slideInLeft 0.8s ease-out 0.6s both",
									}}
								>
									<Button
										variant="contained"
										size="large"
										color="secondary"
										startIcon={<Rocket />}
										onClick={() => navi("/courses")}
										sx={{
											fontWeight: 700,
											fontSize: "0.95rem",
											px: 3.5,
											py: 1.5,
											borderRadius: 2,
											textTransform: "none",
											color: theme.palette.primary.main,
											boxShadow: `0 8px 24px ${theme.palette.secondary.main}40`,
											transition: "all 0.3s ease",
											position: "relative",
											overflow: "hidden",
											"&::before": {
												content: '""',
												position: "absolute",
												top: 0,
												left: "-100%",
												width: "100%",
												height: "100%",
												background: `linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)`,
												transition: "left 0.5s",
											},
											"&:hover": {
												transform: "translateY(-3px)",
												boxShadow: `0 12px 32px ${theme.palette.secondary.main}60`,
												"&::before": {
													left: "100%",
												}
											}
										}}
									>
										Explore Courses
									</Button>
									<Button
										variant="outlined"
										size="large"
										startIcon={<Assignment />}
										onClick={() => navi('/admission')}
										sx={{
											fontWeight: 700,
											fontSize: "0.95rem",
											px: 3.5,
											py: 1.5,
											borderRadius: 2,
											textTransform: "none",
											borderWidth: 2,
											borderColor: "rgba(255, 255, 255, 0.4)",
											color: "#ffffff",
											backdropFilter: "blur(10px)",
											background: "rgba(255, 255, 255, 0.08)",
											transition: "all 0.3s ease",
											"&:hover": {
												borderWidth: 2,
												borderColor: theme.palette.secondary.main,
												background: "rgba(255, 255, 255, 0.15)",
												transform: "translateY(-3px)"
											}
										}}
									>
										Join Now
									</Button>
								</Stack>
							</Stack>
						</Grid>

						{/* Right Panel: Compact Stats + Notifications */}
						<Grid item xs={12} md={5}>
							<Stack
								spacing={2}
								sx={{
									animation: "slideInRight 0.8s ease-out 0.4s both",
									"@keyframes slideInRight": {
										from: { opacity: 0, transform: "translateX(50px)" },
										to: { opacity: 1, transform: "translateX(0)" }
									}
								}}
							>
								{/* Compact inline stats grid */}
								<Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.5 }}>
									{stats.map((stat, index) => {
										const Icon = stat.icon;
										return (
											<Card
												key={index}
												sx={{
													background: "rgba(255,255,255,0.1)",
													backdropFilter: "blur(20px)",
													border: `1px solid ${theme.palette.secondary.main}30`,
													borderRadius: 2,
													transition: "all 0.3s ease",
													"&:hover": {
														background: "rgba(255,255,255,0.15)",
														boxShadow: `0 4px 20px ${theme.palette.secondary.main}30`,
													}
												}}
											>
												<CardContent sx={{ p: "24px !important" }}>
													<Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="center">
														<Box
															sx={{
																width: 56,
																height: 56,
																flexShrink: 0,
																borderRadius: 2,
																background: `linear-gradient(135deg, ${theme.palette.secondary.main}30, ${theme.palette.info.main}30)`,
																display: "flex",
																alignItems: "center",
																justifyContent: "center",
															}}
														>
															<Icon sx={{ fontSize: "1.8rem", color: theme.palette.secondary.main }} />
														</Box>
														<Box>
															<Typography sx={{ fontSize: "2rem", fontWeight: 900, color: "#fff", lineHeight: 1, mb: 0.5 }}>
																{stat.number}
															</Typography>
															<Typography sx={{ fontSize: "0.8rem", color: "rgba(255,255,255,0.7)", fontWeight: 500 }}>
																{stat.label}
															</Typography>
														</Box>
													</Stack>
												</CardContent>
											</Card>
										);
									})}
								</Box>

								{/* Notifications Box */}
								<Card
									sx={{
										background: "rgba(255,255,255,0.1)",
										backdropFilter: "blur(20px)",
										border: `1px solid ${theme.palette.secondary.main}30`,
										borderRadius: 2,
										overflowY: "auto",
										maxHeight: 260,
									}}
								>
									{/* Header */}
									<Box
										sx={{
											px: 2, py: 1.2,
											background: `linear-gradient(90deg, ${theme.palette.secondary.main}25, ${theme.palette.info.main}20)`,
											borderBottom: `1px solid ${theme.palette.secondary.main}25`,
											position: "sticky",
											top: 0,
											zIndex: 1,
											display: "flex",
											alignItems: "center",
											gap: 1,
										}}
									>
										<NotificationsActive sx={{ color: theme.palette.secondary.main, fontSize: "1.1rem" }} />
										<Typography sx={{ color: theme.palette.secondary.main, fontWeight: 700, fontSize: "0.8rem", letterSpacing: "0.5px" }}>
											NOTICES & NOTIFICATIONS
										</Typography>
										{notices.length > 0 && (
											<Chip label={notices.length} size="small" sx={{ height: 18, fontSize: "0.65rem", background: theme.palette.secondary.main, color: theme.palette.primary.main, fontWeight: 700, ml: "auto" }} />
										)}
									</Box>

									{/* Notice items */}
									{noticeLoading ? (
										<Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
											<CircularProgress size={20} sx={{ color: theme.palette.secondary.main }} />
										</Box>
									) : notices.length === 0 ? (
										<Typography sx={{ px: 2, py: 1.5, color: "rgba(255,255,255,0.5)", fontSize: "0.8rem" }}>
											No notices at this time.
										</Typography>
									) : (
										<Stack divider={<Divider sx={{ borderColor: "rgba(255,255,255,0.08)" }} />}>
											{notices.map((notice) => (
												<Box
													key={notice.notice_id}
													onClick={() => setSelectedNotice(notice)}
													sx={{
														px: 2, py: 1.2,
														cursor: "pointer",
														display: "flex",
														alignItems: "flex-start",
														gap: 1,
														transition: "background 0.2s ease",
														"&:hover": { background: "rgba(255,255,255,0.06)" },
													}}
												>
													<Box
														sx={{
															width: 6,
															height: 6,
															borderRadius: "50%",
															flexShrink: 0,
															mt: "5px",
															background: NOTICE_TYPE_COLORS[notice.notice_type] || theme.palette.secondary.main,
														}}
													/>
													<Box sx={{ flex: 1, minWidth: 0 }}>
														<Typography
															sx={{
																color: "rgba(255,255,255,0.9)",
																fontSize: "0.8rem",
																fontWeight: 600,
																whiteSpace: "nowrap",
																overflow: "hidden",
																textOverflow: "ellipsis",
															}}
														>
															{notice.notice_title}
														</Typography>
														<Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "0.65rem", mt: 0.2 }}>
															{notice.notice_created_at}
														</Typography>
													</Box>
													<Chip
														label={notice.notice_type}
														size="small"
														sx={{
															height: 16,
															fontSize: "0.6rem",
															fontWeight: 700,
															background: `${NOTICE_TYPE_COLORS[notice.notice_type]}30`,
															color: NOTICE_TYPE_COLORS[notice.notice_type] || "#fff",
															flexShrink: 0,
														}}
													/>
												</Box>
											))}
										</Stack>
									)}
								</Card>
							</Stack>
						</Grid>
					</Grid>
				</Container>
			</Box>

			{/* Notice Detail Dialog */}
			<Dialog open={Boolean(selectedNotice)} fullWidth maxWidth="sm" onClose={() => setSelectedNotice(null)}>
				{selectedNotice && (
					<>
						<DialogTitle sx={{ display: "flex", alignItems: "flex-start", gap: 1.5, pb: 1 }}>
							<Box sx={{ flex: 1 }}>
								<Typography fontWeight={700} fontSize={17} mb={0.5}>{selectedNotice.notice_title}</Typography>
								<Stack direction="row" spacing={1} alignItems="center">
									<Chip
										label={selectedNotice.notice_type}
										size="small"
										sx={{ height: 20, fontSize: "0.7rem", fontWeight: 700, background: `${NOTICE_TYPE_COLORS[selectedNotice.notice_type]}20`, color: NOTICE_TYPE_COLORS[selectedNotice.notice_type] }}
									/>
									<Typography fontSize={11} color="text.secondary">{selectedNotice.notice_created_at}</Typography>
								</Stack>
							</Box>
							<IconButton size="small" onClick={() => setSelectedNotice(null)}>✕</IconButton>
						</DialogTitle>
						<Divider />
						<DialogContent>
							<Box
								dangerouslySetInnerHTML={{ __html: selectedNotice.notice_content }}
								sx={{
									"& p": { lineHeight: 1.8, fontSize: "0.95rem", color: "text.primary" },
									"& strong": { fontWeight: 700 },
									"& a": { color: "primary.main" },
								}}
							/>
						</DialogContent>
					</>
				)}
			</Dialog>
			<Box
				sx={{
					py: { xs: 6, md: 10 },
					background: '#fff',
					position: 'relative'
				}}
			>
				<Container maxWidth="lg">
					<Stack>
						{/* Section Header */}
						<Stack mt={3} mb={3} alignItems="center" sx={{ textAlign: 'center', maxWidth: '700px', mx: 'auto' }}>
							<Chip
								label="OUR PROGRAMS"
								sx={{
									mb: 2,
									background: `${theme.palette.primary.main}15`,
									color: theme.palette.primary.main,
									fontWeight: 700,
									fontSize: '0.75rem',
									letterSpacing: '1px',
									border: 'none',
									boxShadow: `0 2px 8px ${theme.palette.primary.main}15`
								}}
							/>
							<Typography
								sx={{
									fontSize: { xs: '1.75rem', md: '2.5rem' },
									fontWeight: 800,
									color: theme.palette.text.primary,
									mb: 1.5,
									lineHeight: 1.2
								}}
							>
								Popular Courses
							</Typography>
							<Typography
								sx={{
									fontSize: { xs: '0.95rem', md: '1.05rem' },
									color: theme.palette.text.secondary,
									lineHeight: 1.6
								}}
							>
								Explore our most sought-after programs designed to accelerate your career
							</Typography>
						</Stack>


						{/* Courses Grid */}
						<Grid container spacing={3}>
							{[
								{
									title: "ADCA + AI",
									subtitle: "Advanced Diploma in Computer Application + AI",
									description: "Master advanced computing with MS Office, Tally, Internet, DTP, and AI fundamentals",
									icon: School,
									color: theme.palette.info.main,
									tags: ["MS Office", "Tally", "DTP", "AI"]
								},
								{
									title: "Web Development",
									subtitle: "Full Stack Web Development",
									description: "Build modern websites and web applications with HTML, CSS, JavaScript, React, and backend technologies",
									icon: Rocket,
									color: theme.palette.secondary.main,
									tags: ["HTML", "CSS", "JavaScript", "React"]
								},
								{
									title: "MDOM",
									subtitle: "Master Diploma in Office Management",
									description: "Comprehensive training in office automation, management software, and business applications",
									icon: BusinessCenter,
									color: theme.palette.success.main,
									tags: ["Excel", "PowerPoint", "Accounting", "Management"]
								},
								{
									title: "Artificial Intelligence",
									subtitle: "AI & Machine Learning",
									description: "Dive into AI, machine learning, neural networks, and deep learning to build intelligent systems",
									icon: Psychology,
									color: theme.palette.warning.main,
									tags: ["Python", "TensorFlow", "ML", "AI"]
								},
							].map((course, index) => {
								const Icon = course.icon;
								return (
									<Grid item xs={12} sm={6} md={3} key={index}>
										<Card
											sx={{
												height: '100%',
												borderRadius: 3,
												transition: 'all 0.3s ease',
												cursor: 'pointer',
												background: '#fff',
												boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
												'&:hover': {
													transform: 'translateY(-8px)',
													boxShadow: `0 12px 32px ${course.color}25`,
													'& .course-icon': {
														transform: 'scale(1.1)',
														background: course.color
													},
													'& .course-icon svg': {
														color: '#fff'
													}
												}
											}}
										>
											<CardContent sx={{ p: 3 }}>
												<Stack spacing={2.5}>
													{/* Icon */}
													<Box
														className="course-icon"
														sx={{
															width: 60,
															height: 60,
															borderRadius: 2,
															background: `${course.color}15`,
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															transition: 'all 0.3s ease'
														}}
													>
														<Icon sx={{ fontSize: '1.75rem', color: course.color, transition: 'all 0.3s ease' }} />
													</Box>

													{/* Content */}
													<Box>
														<Typography
															sx={{
																fontSize: '1.4rem',
																fontWeight: 700,
																color: theme.palette.text.primary,
																mb: 0.5,
																lineHeight: 1.2
															}}
														>
															{course.title}
														</Typography>
														<Typography
															sx={{
																fontSize: '0.8rem',
																color: course.color,
																fontWeight: 600,
																mb: 1.5,
																letterSpacing: '0.3px'
															}}
														>
															{course.subtitle}
														</Typography>
														<Typography
															sx={{
																fontSize: '0.875rem',
																color: theme.palette.text.secondary,
																lineHeight: 1.6,
																mb: 2
															}}
														>
															{course.description}
														</Typography>

														{/* Tags */}
														<Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
															{course.tags.map((tag, i) => (
																<Chip
																	key={i}
																	label={tag}
																	size="small"
																	sx={{
																		height: 24,
																		fontSize: '0.7rem',
																		fontWeight: 600,
																		background: theme.palette.grey[100],
																		color: theme.palette.text.secondary,
																		border: 'none',
																		boxShadow: '0 1px 3px rgba(0, 0, 0, 0.08)',
																		'&:hover': {
																			background: `${course.color}15`,
																			color: course.color,
																			boxShadow: `0 2px 6px ${course.color}20`
																		}
																	}}
																/>
															))}
														</Box>
													</Box>
												</Stack>
											</CardContent>
										</Card>
									</Grid>
								);
							})}
						</Grid>
					</Stack>
				</Container>
			</Box>



			{/* Why Choose Us Section */}
			<Box
				sx={{
					py: { xs: 6, md: 10 },
					background: theme.palette.grey[50]
				}}
			>
				<Container maxWidth="lg">
					<Stack>
						{/* Section Header */}
						<Stack alignItems="center" sx={{ textAlign: 'center', maxWidth: '700px', mx: 'auto' }}>
							<Chip
								label="WHY CHOOSE US"
								sx={{
									mb: 2,
									background: `${theme.palette.primary.main}15`,
									color: theme.palette.primary.main,
									fontWeight: 700,
									fontSize: '0.75rem',
									letterSpacing: '1px',
									border: 'none',
									boxShadow: `0 2px 8px ${theme.palette.primary.main}15`
								}}
							/>
							<Typography
								sx={{
									fontSize: { xs: '1.75rem', md: '2.5rem' },
									fontWeight: 800,
									color: theme.palette.text.primary,
									mb: 1.5,
									lineHeight: 1.2
								}}
							>
								Excellence in Education
							</Typography>
							<Typography
								sx={{
									fontSize: { xs: '0.95rem', md: '1.05rem' },
									color: theme.palette.text.secondary,
									lineHeight: 1.6
								}}
							>
								Join thousands of students who have transformed their careers with our programs
							</Typography>
						</Stack>

						{/* Features Grid */}
						<Grid container mt={3} spacing={3}>
							{[
								{
									number: "01",
									title: "Expert Instructors",
									description: "Learn from industry professionals with years of real-world experience",
									icon: School,
									color: theme.palette.primary.main
								},
								{
									number: "02",
									title: "Hands-On Training",
									description: "Practical projects and real-world applications to build your portfolio",
									icon: Rocket,
									color: theme.palette.info.main
								},
								{
									number: "03",
									title: "Industry Recognition",
									description: "Certificates recognized by leading companies and organizations",
									icon: EmojiEvents,
									color: theme.palette.warning.main
								},
								{
									number: "04",
									title: "Career Support",
									description: "Placement assistance and career guidance to help you succeed",
									icon: TrendingUp,
									color: theme.palette.success.main
								},
							].map((feature, index) => {
								const Icon = feature.icon;
								return (
									<Grid item xs={12} sm={6} md={3} key={index}>
										<Card
											sx={{
												height: '100%',
												background: '#fff',
												borderRadius: 3,
												transition: 'all 0.3s ease',
												cursor: 'pointer',
												boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
												'&:hover': {
													transform: 'translateY(-8px)',
													boxShadow: `0 12px 32px ${feature.color}25`,
													'& .feature-number': {
														background: feature.color,
														color: '#fff',
														transform: 'scale(1.1)'
													},
													'& .feature-icon': {
														transform: 'scale(1.1)',
														color: feature.color
													}
												}
											}}
										>
											<CardContent sx={{ p: 3 }}>
												<Stack spacing={2.5} alignItems="center" textAlign="center">
													{/* Number Badge */}
													<Box
														className="feature-number"
														sx={{
															width: 50,
															height: 50,
															borderRadius: '50%',
															background: `${feature.color}15`,
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															fontSize: '1.1rem',
															fontWeight: 800,
															color: feature.color,
															transition: 'all 0.3s ease'
														}}
													>
														{feature.number}
													</Box>

													{/* Icon */}
													<Icon
														className="feature-icon"
														sx={{
															fontSize: '2.5rem',
															color: feature.color,
															transition: 'all 0.3s ease'
														}}
													/>

													{/* Content */}
													<Box>
														<Typography
															sx={{
																fontSize: '1.25rem',
																fontWeight: 700,
																color: theme.palette.text.primary,
																mb: 1,
																lineHeight: 1.3
															}}
														>
															{feature.title}
														</Typography>
														<Typography
															sx={{
																fontSize: '0.875rem',
																color: theme.palette.text.secondary,
																lineHeight: 1.6
															}}
														>
															{feature.description}
														</Typography>
													</Box>
												</Stack>
											</CardContent>
										</Card>
									</Grid>
								);
							})}
						</Grid>
					</Stack>
				</Container>
			</Box>

			{/* Certifications Section */}
			<Box
				sx={{
					py: { xs: 6, md: 10 },
					background: '#fff'
				}}
			>
				<Container maxWidth="lg">
					<Stack>
						{/* Section Header */}
						<Stack alignItems="center" sx={{ textAlign: 'center', maxWidth: '700px', mx: 'auto' }}>
							<Chip
								label="CERTIFICATIONS & ACCREDITATIONS"
								sx={{
									mb: 2,
									background: `${theme.palette.primary.main}15`,
									color: theme.palette.primary.main,
									fontWeight: 700,
									fontSize: '0.75rem',
									letterSpacing: '1px',
									border: 'none',
									boxShadow: `0 2px 8px ${theme.palette.primary.main}15`
								}}
							/>
							<Typography
								sx={{
									fontSize: { xs: '1.75rem', md: '2.5rem' },
									fontWeight: 800,
									color: theme.palette.text.primary,
									mb: 1.5,
									lineHeight: 1.2
								}}
							>
								Trusted & Recognized
							</Typography>
							<Typography
								sx={{
									fontSize: { xs: '0.95rem', md: '1.05rem' },
									color: theme.palette.text.secondary,
									lineHeight: 1.6
								}}
							>
								Our commitment to quality education is backed by industry-recognized certifications
							</Typography>
						</Stack>

						{/* Certifications Grid */}
						<Grid container mt={3} spacing={3}>
							{[
								{
									title: "ISO Certified",
									subtitle: "Quality Management",
									description: "ISO certified institution ensuring international quality standards in education",
									icon: VerifiedUser,
									badge: "ISO 9001:2015",
									color: theme.palette.primary.main
								},
								{
									title: "MSME Registered",
									subtitle: "Government Recognition",
									description: "Registered under Ministry of Micro, Small & Medium Enterprises",
									icon: AccountBalance,
									badge: "MSME",
									color: theme.palette.info.main
								},
								{
									title: "Private Limited",
									subtitle: "Legally Incorporated",
									description: "Registered Private Limited Company with proper legal framework",
									icon: Business,
									badge: "PVT LTD",
									color: theme.palette.success.main
								},
								{
									title: "Government Recognized",
									subtitle: "Official Approval",
									description: "Recognized by government authorities for quality education",
									icon: Star,
									badge: "CERTIFIED",
									color: theme.palette.warning.main
								},
							].map((cert, index) => {
								const Icon = cert.icon;
								return (
									<Grid item xs={12} sm={6} md={3} key={index}>
										<Card
											sx={{
												height: '100%',
												background: '#fff',
												borderRadius: 3,
												transition: 'all 0.3s ease',
												cursor: 'pointer',
												boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
												'&:hover': {
													transform: 'translateY(-8px)',
													boxShadow: `0 12px 32px ${cert.color}25`,
													'& .cert-badge': {
														background: cert.color,
														color: '#fff',
														borderColor: cert.color
													},
													'& .cert-icon': {
														transform: 'scale(1.1) rotate(5deg)',
														background: cert.color
													},
													'& .cert-icon svg': {
														color: '#fff'
													}
												}
											}}
										>
											<CardContent sx={{ p: 3 }}>
												<Stack spacing={2.5} alignItems="center" textAlign="center">
													{/* Badge */}
													<Chip
														label={cert.badge}
														className="cert-badge"
														size="small"
														sx={{
															height: 28,
															fontSize: '0.7rem',
															fontWeight: 700,
															background: `${cert.color}15`,
															color: cert.color,
															letterSpacing: '0.5px',
															border: `1px solid ${cert.color}30`,
															transition: 'all 0.3s ease'
														}}
													/>

													{/* Icon */}
													<Box
														className="cert-icon"
														sx={{
															width: 70,
															height: 70,
															borderRadius: 2,
															background: `${cert.color}15`,
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															transition: 'all 0.3s ease'
														}}
													>
														<Icon sx={{ fontSize: '2rem', color: cert.color, transition: 'all 0.3s ease' }} />
													</Box>

													{/* Content */}
													<Box>
														<Typography
															sx={{
																fontSize: '1.25rem',
																fontWeight: 700,
																color: theme.palette.text.primary,
																mb: 0.5,
																lineHeight: 1.3
															}}
														>
															{cert.title}
														</Typography>
														<Typography
															sx={{
																fontSize: '0.8rem',
																color: cert.color,
																fontWeight: 600,
																mb: 1.5,
																letterSpacing: '0.3px'
															}}
														>
															{cert.subtitle}
														</Typography>
														<Typography
															sx={{
																fontSize: '0.875rem',
																color: theme.palette.text.secondary,
																lineHeight: 1.6
															}}
														>
															{cert.description}
														</Typography>
													</Box>
												</Stack>
											</CardContent>
										</Card>
									</Grid>
								);
							})}
						</Grid>
					</Stack>
				</Container>
			</Box>


			{/* Student Reviewed Section */}
			<Box
				sx={{
					py: { xs: 6, md: 5 },
					background: '#fff'
				}}
			>
				<Container maxWidth="lg">
					<Stack>
						{/* Section Header */}
						<Stack alignItems="center" sx={{ textAlign: 'center', maxWidth: '700px', mx: 'auto' }}>
							<Chip
								label="STUDENT REVIEWS"
								sx={{
									mb: 2,
									background: `${theme.palette.primary.main}15`,
									color: theme.palette.primary.main,
									fontWeight: 700,
									fontSize: '0.75rem',
									letterSpacing: '1px',
									border: 'none',
									boxShadow: `0 2px 8px ${theme.palette.primary.main}15`
								}}
							/>
							<Typography
								sx={{
									fontSize: { xs: '1.75rem', md: '2.5rem' },
									fontWeight: 800,
									color: theme.palette.text.primary,
									mb: 1.5,
									lineHeight: 1.2
								}}
							>
								What Our Students Say
							</Typography>
							<Typography
								sx={{
									fontSize: { xs: '0.95rem', md: '1.05rem' },
									color: theme.palette.text.secondary,
									lineHeight: 1.6
								}}
							>
								Real feedback from students who have transformed their careers with us
							</Typography>
						</Stack>

						{/* Reviews Grid */}
						<Grid container mt={3} spacing={3}>
							{reviews.map((review, index) => (
								<Grid item xs={12} sm={6} md={3} key={index}>
									<Card
										sx={{
											height: '100%',
											background: '#fff',
											borderRadius: 3,
											transition: 'all 0.3s ease',
											cursor: 'pointer',
											boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
											'&:hover': {
												transform: 'translateY(-8px)',
												boxShadow: `0 12px 32px ${review.color}25`,
												'& .review-avatar': {
													transform: 'scale(1.1)',
													background: review.color,
													color: '#fff'
												}
											}
										}}
									>
										<CardContent sx={{ p: 3 }}>
											<Stack spacing={2}>
												{/* Avatar and Student Info */}
												<Stack direction="row" spacing={1.5} alignItems="center">
													<Box
														className="review-avatar"
														sx={{
															width: 50,
															height: 50,
															borderRadius: '50%',
															background: `${review.color}15`,
															display: 'flex',
															alignItems: 'center',
															justifyContent: 'center',
															fontSize: '1.1rem',
															fontWeight: 700,
															color: review.color,
															transition: 'all 0.3s ease',
															flexShrink: 0
														}}
													>
														<img src={`/spywareassets/student/${review.studentid}.jpg`} alt={review.student_name} style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }} />
													</Box>
													<Box sx={{ flex: 1, minWidth: 0 }}>
														<Typography
															sx={{
																fontSize: '0.95rem',
																fontWeight: 700,
																color: theme.palette.text.primary,
																lineHeight: 1.2,
																mb: 0.3
															}}
														>
															{review.student_name}
														</Typography>
														<Chip
															label={review.course_title}
															size="small"
															sx={{
																height: 20,
																fontSize: '0.65rem',
																fontWeight: 600,
																background: `${review.color}15`,
																color: review.color,
																border: 'none'
															}}
														/>
													</Box>
												</Stack>

												{/* Rating */}
												<Box sx={{ display: 'flex', gap: 0.5 }}>
													{[...Array(review.rating)].map((_, i) => (
														<Star key={i} sx={{ fontSize: '1rem', color: theme.palette.warning.main }} />
													))}
												</Box>

												{/* Review Text */}
												<Typography
													sx={{
														fontSize: '0.875rem',
														color: theme.palette.text.secondary,
														lineHeight: 1.6,
														minHeight: '100px'
													}}
												>
													"{review.review}"
												</Typography>
											</Stack>
										</CardContent>
									</Card>
								</Grid>
							))}

							{/* Total Reviews Card */}
							<Grid item xs={12} sm={6} md={3}>
								<Card
									sx={{
										height: '100%',
										background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
										borderRadius: 3,
										transition: 'all 0.3s ease',
										cursor: 'pointer',
										boxShadow: '0 2px 12px rgba(0, 0, 0, 0.06)',
										'&:hover': {
											transform: 'translateY(-8px)',
											boxShadow: `0 12px 32px ${theme.palette.primary.main}40`
										}
									}}
								>
									<CardContent sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', textAlign: 'center' }}>
										<Stack spacing={2} alignItems="center">
											{/* Icon */}
											<RateReview sx={{ fontSize: '3rem', color: '#fff' }} />

											{/* Total Count */}
											<Typography
												sx={{
													fontSize: '2.5rem',
													fontWeight: 900,
													color: '#fff',
													lineHeight: 1
												}}
											>
												{totalReviews}
											</Typography>

											<Typography
												sx={{
													fontSize: '1rem',
													fontWeight: 600,
													color: 'rgba(255, 255, 255, 0.9)',
													mb: 1
												}}
											>
												Total Reviews
											</Typography>

											{/* View All Button */}
											<NavLink to="/reviews">
												<Button
													variant="contained"
													color="secondary"
													endIcon={<ArrowForward />}
													sx={{
														fontWeight: 700,
														fontSize: '0.85rem',
														px: 3,
														py: 1,
														borderRadius: 2,
														textTransform: 'none',
														boxShadow: '0 4px 16px rgba(0, 0, 0, 0.2)',
														'&:hover': {
															transform: 'scale(1.05)',
															boxShadow: '0 6px 20px rgba(0, 0, 0, 0.3)'
														}
													}}
												>
													View All
												</Button>
											</NavLink>
										</Stack>
									</CardContent>
								</Card>
							</Grid>
						</Grid>
					</Stack>
				</Container>
			</Box>


			{/* Director's Message Section */}
			<Box
				sx={{
					py: { xs: 6, md: 10 },
					background: '#fff',
					position: 'relative'
				}}
			>
				<Container maxWidth="lg">
					<Stack spacing={5}>
						{/* Section Header */}
						<Stack alignItems="center" sx={{ textAlign: 'center', maxWidth: '700px', mx: 'auto' }}>
							<Chip
								label="DIRECTOR'S MESSAGE"
								sx={{
									mb: 2,
									background: `${theme.palette.primary.main}15`,
									color: theme.palette.primary.main,
									fontWeight: 700,
									fontSize: '0.75rem',
									letterSpacing: '1px',
									border: 'none',
									boxShadow: `0 2px 8px ${theme.palette.primary.main}15`
								}}
							/>
							<Typography
								sx={{
									fontSize: { xs: '1.75rem', md: '2.5rem' },
									fontWeight: 800,
									color: theme.palette.text.primary,
									mb: 1.5,
									lineHeight: 1.2
								}}
							>
								Words from Our Director
							</Typography>
							<Typography
								sx={{
									fontSize: { xs: '0.95rem', md: '1.05rem' },
									color: theme.palette.text.secondary,
									lineHeight: 1.6
								}}
							>
								Leading with vision, inspiring excellence
							</Typography>
						</Stack>

						{/* Director Message Card */}
						<Box sx={{ position: 'relative', mt: { xs: 8, md: 6 } }}>
							<Card
								sx={{
									background: `linear-gradient(135deg, ${theme.palette.grey[50]} 0%, #fff 100%)`,
									borderRadius: 4,
									boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
									border: `1px solid ${theme.palette.grey[200]}`,
									position: 'relative',
									overflow: 'visible',
									transition: 'all 0.3s ease',
									'&:hover': {
										boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
									}
								}}
							>
								{/* Decorative Gradient Bar */}
								<Box
									sx={{
										position: 'absolute',
										top: 0,
										left: 0,
										right: 0,
										height: 6,
										background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
										borderRadius: '16px 16px 0 0'
									}}
								/>

								<CardContent sx={{ p: { xs: 3, md: 5 }, pt: { xs: 4, md: 6 } }}>
									<Grid container spacing={4}>
										{/* Director Image - Overlapping */}
										<Grid item xs={12} md={4}>
											<Box
												sx={{
													position: { xs: 'relative', md: 'absolute' },
													top: { md: -40 },
													left: { md: 40 },
													display: 'flex',
													justifyContent: { xs: 'center', md: 'flex-start' },
													mb: { xs: 2, md: 0 }
												}}
											>
												<Box
													sx={{
														position: 'relative',
														width: { xs: 200, md: 280 },
														height: { xs: 200, md: 280 }
													}}
												>
													{/* Image Container */}
													<Box
														sx={{
															width: '100%',
															height: '100%',
															borderRadius: 3,
															overflow: 'hidden',
															boxShadow: `0 16px 48px ${theme.palette.primary.main}30`,
															border: `5px solid #fff`,
															position: 'relative',
															transition: 'all 0.3s ease',
															'&:hover': {
																transform: 'translateY(-8px)',
																boxShadow: `0 20px 56px ${theme.palette.primary.main}40`
															}
														}}
													>
														<Box
															component="img"
															src="https://www.spywarecomputers.com/spywareassets/gallery/admin_profile.jpg"
															alt="Director"
															sx={{
																width: '100%',
																height: '100%',
																objectFit: 'cover'
															}}
														/>
													</Box>

													{/* Decorative Corner Element */}
													<Box
														sx={{
															position: 'absolute',
															bottom: -10,
															right: -10,
															width: 80,
															height: 80,
															background: `linear-gradient(135deg, ${theme.palette.secondary.main}, ${theme.palette.primary.main})`,
															borderRadius: '50%',
															opacity: 0.15,
															zIndex: -1
														}}
													/>
												</Box>
											</Box>
										</Grid>

										{/* Message Content */}
										<Grid item xs={12} md={8}>
											<Stack spacing={3} sx={{ pl: { md: 2 }, pt: { xs: 2, md: 0 } }}>
												{/* Quote Icon */}
												<Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
													<FormatQuote
														sx={{
															fontSize: '4rem',
															color: theme.palette.primary.main,
															opacity: 0.2,
															transform: 'rotate(180deg)',
															mr: -2,
															mt: -2
														}}
													/>
												</Box>

												{/* Message Text */}
												<Typography
													sx={{
														fontSize: { xs: '1rem', md: '1.15rem' },
														lineHeight: 1.8,
														color: theme.palette.text.primary,
														position: 'relative',
														pl: 2,
														borderLeft: `4px solid ${theme.palette.primary.main}`,
														fontWeight: 400
													}}
												>
													Welcome to Spyware Computer Education, where we are committed to shaping the future of IT professionals through comprehensive training and education. As the Director of this esteemed institute, it gives me great pleasure to extend a warm welcome to all aspiring learners.
												</Typography>

												{/* Director Signature */}
												<Box
													sx={{
														mt: 3,
														pt: 3,
														borderTop: `2px solid ${theme.palette.grey[200]}`,
														display: 'flex',
														alignItems: 'center',
														gap: 2
													}}
												>
													<Box sx={{ flex: 1 }}>
														<Typography
															sx={{
																fontSize: '1.4rem',
																fontWeight: 800,
																color: theme.palette.text.primary,
																mb: 0.5,
																lineHeight: 1.2
															}}
														>
															Vibhash Kumar Jha
														</Typography>
														<Typography
															sx={{
																fontSize: '1rem',
																color: theme.palette.primary.main,
																fontWeight: 600,
																letterSpacing: '0.5px'
															}}
														>
															Director & Founder
														</Typography>
													</Box>

													{/* Signature Line */}
													<Box
														sx={{
															width: 120,
															height: 2,
															background: `linear-gradient(90deg, ${theme.palette.primary.main}, transparent)`,
															display: { xs: 'none', sm: 'block' }
														}}
													/>
												</Box>
											</Stack>
										</Grid>
									</Grid>
								</CardContent>
							</Card>
						</Box>
					</Stack>
				</Container>
			</Box>


			{/* Teachers Section */}
			{teachers.length > 0 && (
				<Box
					sx={{
						py: { xs: 6, md: 10 },
						background: theme.palette.grey[50],
						position: 'relative',
						overflow: 'hidden',
						'& .swiper-pagination-bullet': {
							background: theme.palette.primary.main,
							opacity: 0.4,
						},
						'& .swiper-pagination-bullet-active': {
							opacity: 1,
							width: '24px',
							borderRadius: '4px',
						},
					}}
				>
					<Container maxWidth="lg">
						{/* Section Header */}
						<Stack alignItems="center" sx={{ textAlign: 'center', maxWidth: '700px', mx: 'auto', mb: 5 }}>
							<Chip
								label="MEET OUR FACULTY"
								sx={{
									mb: 2,
									background: `${theme.palette.primary.main}15`,
									color: theme.palette.primary.main,
									fontWeight: 700,
									fontSize: '0.75rem',
									letterSpacing: '1px',
									border: 'none',
									boxShadow: `0 2px 8px ${theme.palette.primary.main}15`
								}}
							/>
							<Typography
								sx={{
									fontSize: { xs: '1.75rem', md: '2.5rem' },
									fontWeight: 800,
									color: theme.palette.text.primary,
									mb: 1.5,
									lineHeight: 1.2
								}}
							>
								Our Expert Teachers
							</Typography>
							<Typography
								sx={{
									fontSize: { xs: '0.95rem', md: '1.05rem' },
									color: theme.palette.text.secondary,
									lineHeight: 1.6
								}}
							>
								Learn from experienced professionals dedicated to your success
							</Typography>
						</Stack>

						<Swiper
							modules={[Autoplay, Pagination]}
							spaceBetween={24}
							slidesPerView={1}
							breakpoints={{
								600: { slidesPerView: 2 },
								900: { slidesPerView: 3 },
							}}
							autoplay={{ delay: 3000, disableOnInteraction: false }}
							pagination={{ clickable: true, dynamicBullets: true }}
							loop={teachers.length > 2}
							style={{ paddingBottom: '48px' }}
						>
							{teachers.map((teacher) => (
								<SwiperSlide key={teacher.teacher_id}>
									<Card
										sx={{
											height: '100%',
											background: '#fff',
											borderRadius: 4,
											boxShadow: '0 4px 20px rgba(0,0,0,0.07)',
											transition: 'all 0.3s ease',
											overflow: 'hidden',
											'&:hover': {
												transform: 'translateY(-8px)',
												boxShadow: `0 16px 40px ${theme.palette.primary.main}20`,
											},
										}}
									>
										{/* Top Gradient Bar */}
										<Box
											sx={{
												height: 5,
												background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
											}}
										/>
										<CardContent sx={{ p: 3 }}>
											<Stack spacing={2.5} alignItems="center" textAlign="center">
												{/* Avatar */}
												<Box
													sx={{
														width: 90,
														height: 90,
														borderRadius: '50%',
														background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
														display: 'flex',
														alignItems: 'center',
														justifyContent: 'center',
														boxShadow: `0 8px 24px ${theme.palette.primary.main}30`,
														flexShrink: 0,
														overflow: 'hidden',
													}}
												>
													<img src={`${assetsURL}/gallery/${teacher.teacher_id}.jpg`} alt={teacher.name} />
												</Box>

												{/* Name & Experience */}
												<Box>
													<Typography
														sx={{
															fontSize: '1.2rem',
															fontWeight: 700,
															color: theme.palette.text.primary,
															mb: 0.5,
														}}
													>
														{teacher.name}
													</Typography>
													<Chip
														icon={<WorkspacePremium sx={{ fontSize: '0.9rem !important', color: `${theme.palette.secondary.main} !important` }} />}
														label={`${teacher.experience_year} ${teacher.experience_year === 1 ? 'Year' : 'Years'} Experience`}
														size="small"
														sx={{
															background: `${theme.palette.secondary.main}15`,
															color: theme.palette.secondary.main,
															fontWeight: 600,
															fontSize: '0.72rem',
															border: 'none',
														}}
													/>
												</Box>

												{/* Divider */}
												<Box
													sx={{
														width: '60%',
														height: 1,
														background: theme.palette.grey[200],
													}}
												/>

												{/* Description */}
												<Typography
													sx={{
														fontSize: '0.875rem',
														color: theme.palette.text.secondary,
														lineHeight: 1.7,
														display: '-webkit-box',
														WebkitLineClamp: 4,
														WebkitBoxOrient: 'vertical',
														overflow: 'hidden',
													}}
												>
													{teacher.description}
												</Typography>
											</Stack>
										</CardContent>
									</Card>
								</SwiperSlide>
							))}
						</Swiper>
					</Container>
				</Box>
			)}


			{/* Call to Action Section */}
			<Box
				sx={{
					position: "relative",
					py: { xs: 10, md: 15 },
					background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
					overflow: "hidden",
				}}
			>
				{/* Decorative Elements */}
				<Box
					sx={{
						position: "absolute",
						top: "-10%",
						right: "-5%",
						width: "400px",
						height: "400px",
						borderRadius: "50%",
						background: `${theme.palette.secondary.main}20`,
						filter: "blur(80px)",
						animation: "float 8s ease-in-out infinite",
						"@keyframes float": {
							"0%, 100%": { transform: "translateY(0px)" },
							"50%": { transform: "translateY(-30px)" }
						}
					}}
				/>
				<Box
					sx={{
						position: "absolute",
						bottom: "-10%",
						left: "-5%",
						width: "300px",
						height: "300px",
						borderRadius: "50%",
						background: `${theme.palette.info.main}20`,
						filter: "blur(80px)",
						animation: "float 6s ease-in-out infinite reverse",
					}}
				/>

				<Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
					<Stack spacing={4} alignItems="center" textAlign="center">
						{/* Badge */}
						<Box
							sx={{
								display: "inline-flex",
								alignItems: "center",
								gap: 1,
								px: 2.5,
								py: 1,
								borderRadius: 50,
								background: "rgba(255, 255, 255, 0.1)",
								border: "1px solid rgba(255, 255, 255, 0.2)",
								backdropFilter: "blur(10px)",
							}}
						>
							<Typography
								sx={{
									color: "#ffffff",
									fontWeight: 600,
									fontSize: { xs: "0.75rem", md: "0.85rem" },
									letterSpacing: "1px",
									textTransform: "uppercase",
								}}
							>
								🚀 Start Your Journey Today
							</Typography>
						</Box>

						{/* Heading */}
						<Typography
							sx={{
								fontSize: { xs: "2rem", md: "3.5rem" },
								fontWeight: 900,
								color: "#ffffff",
								lineHeight: 1.2,
							}}
						>
							Ready to Transform Your Career?
						</Typography>

						{/* Description */}
						<Typography
							sx={{
								fontSize: { xs: "1rem", md: "1.2rem" },
								color: "rgba(255, 255, 255, 0.9)",
								maxWidth: "600px",
								lineHeight: 1.7,
							}}
						>
							Join thousands of students who have already started their journey to success. Enroll now and get access to industry-leading courses.
						</Typography>

						{/* CTA Buttons */}
						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={2}
							sx={{ mt: 2 }}
						>
							<Button
								variant="contained"
								size="large"
								color="secondary"
								startIcon={<Rocket />}
								onClick={() => navi("/courses")}
								sx={{
									fontWeight: 700,
									fontSize: "1rem",
									px: 4,
									py: 1.8,
									borderRadius: 2,
									textTransform: "none",
									color: theme.palette.primary.main,
									boxShadow: "0 8px 32px rgba(255, 255, 255, 0.3)",
									transition: "all 0.3s ease",
									"&:hover": {
										transform: "translateY(-3px)",
										boxShadow: "0 12px 48px rgba(255, 255, 255, 0.4)",
									}
								}}
							>
								Enroll Now
							</Button>
							<Button
								variant="outlined"
								size="large"
								startIcon={<Call />}
								onClick={() => (window.location.href = "tel:7808426759")}
								sx={{
									fontWeight: 700,
									fontSize: "1rem",
									px: 4,
									py: 1.8,
									borderRadius: 2,
									textTransform: "none",
									borderWidth: 2,
									borderColor: "rgba(255, 255, 255, 0.5)",
									color: "#ffffff",
									backdropFilter: "blur(10px)",
									background: "rgba(255, 255, 255, 0.1)",
									transition: "all 0.3s ease",
									"&:hover": {
										borderWidth: 2,
										borderColor: "#ffffff",
										background: "rgba(255, 255, 255, 0.2)",
										transform: "translateY(-3px)"
									}
								}}
							>
								Contact Us
							</Button>
						</Stack>
					</Stack>
				</Container>
			</Box>
		</>
	);
}
